<template>
    <article class="restaurant">
        <RestaurantHero
            :background-color="entry?.restaurantHero?.[0]?.backgroundColor"
            :text-color="entry?.restaurantHero?.[0]?.textColor"
            :images="entry?.restaurantHero?.[0]?.images"
            :logo="entry?.restaurantHero?.[0]?.restaurantLogo?.[0]"
            :description="entry?.restaurantHero?.[0]?.description"
            :pending="pending"
            :call-to-action-text="entry?.restaurantHero?.[0]?.callToActionText"
            :restaurant-id="entry?.formitableRestaurantId"
            :table-booking-provider="entry?.tableBookingProvider"
            :opentable-reservation-link="entry?.opentableReservationLink"
            :secondary-button-link="entry?.restaurantHero?.[0]?.secondaryButtonLink"
        />

        <ScrollNavigation
            :pending="pending"
            :entry="entry"
            :items="scrollItems"
        />

        <IllustrationGrid
            :id="kebabCase(entry?.illustrationGrid?.[0]?.title)"
            class="restaurant__section"
            :items="entry?.illustrationGrid?.[0]?.illustrationItems"
        >
            <template #title>{{ entry?.illustrationGrid?.[0]?.title }}</template>
            <template #description>{{ entry?.illustrationGrid?.[0]?.description }}</template>

            <IllustrationItem
                v-for="illustration in entry?.illustrationGrid?.[0]?.illustrationItems"
                :key="illustration?.title"
                :illustration="illustration"
                :class="illustration?.sticker?.length > 12 ? 'sticker__title--small' : ''"
            >
                <template
                    v-if="illustration?.sticker"
                    #offer
                >
                    {{ illustration?.sticker }}
                </template>
                <template #title>
                    {{ illustration?.title }}
                </template>
                <template #description>
                    {{ illustration?.description }}
                </template>
                <template #cta>
                    {{ illustration?.linkField?.text }}
                </template>
            </IllustrationItem>
        </IllustrationGrid>

        <CarousselBlock
            v-if="entry?.carousselBlock?.[0]?.title || pending"
            :id="kebabCase(entry?.carousselBlock?.[0]?.title)"
            class="restaurant__section"
            :caroussel-items="entry?.carousselBlock?.[0]?.carousselItems"
            :background-color="entry?.carousselBlock?.[0]?.backgroundColor"
            pagination-type="pagination"
            :pending="pending"
        >
            <template
                v-if="entry?.carousselBlock?.[0]?.title"
                #title
            >
                {{ entry?.carousselBlock?.[0]?.title }}
            </template>

            <template #description>
                {{ entry?.carousselBlock?.[0]?.description }}
            </template>
        </CarousselBlock>

        <RelatedItems
            v-if="entry?.relatedItems.length > 0"
            :id="kebabCase(entry?.relatedItems?.[0]?.title)"
            class="restaurant__section"
            :background-color="entry?.relatedItems?.[0]?.backgroundColor"
            :custom-background-color="entry?.relatedItems?.[0]?.customBackgroundColor"
            :items="entry?.relatedItems?.[0]?.entries"
            :pending="pending"
        >
            <template
                v-if="entry?.relatedItems?.[0]?.title"
                #title
            >
                {{ entry?.relatedItems?.[0]?.title }}
            </template>
            <template
                v-if=" entry?.relatedItems?.[0]?.caption"
                #caption
            >
                {{ entry?.relatedItems?.[0]?.caption }}
            </template>
        </RelatedItems>


        <template v-if="entry?.promoBlock?.length || pending">
            <PromoBlock
                v-for="promoBlockRow in entry?.promoBlock"
                :key="promoBlockRow?.title"
                :background-color="promoBlockRow?.backgroundColor"
                :custom-background-color="promoBlockRow?.customBackgroundColor"
                :pending="pending"
                :image="promoBlockRow?.image?.[0]"
                :image-position="promoBlockRow?.imagePosition"
                :link="promoBlockRow?.linkField"
            >
                <template
                    v-if="promoBlockRow?.title"
                    #title
                >
                    {{ promoBlockRow?.title }}
                </template>

                <template
                    v-if="promoBlockRow?.description"
                    #description
                >
                    {{ promoBlockRow?.description }}
                </template>

                <template
                    v-if="promoBlockRow?.richText?.html"
                    #richText
                >
                    <div v-html="promoBlockRow?.richText?.html" />
                </template>


                <template
                    v-if="promoBlockRow?.linkField?.url || promoBlockRow?.linkField?.element"
                    #text
                >
                    <BaseArrowLink
                        :element="CraftLink"
                        :link="promoBlockRow?.linkField"
                        label-position="right"
                    >
                        {{ promoBlockRow?.linkField.text }}
                    </BaseArrowLink>
                </template>
            </PromoBlock>
        </template>

        <IllustrationGrid
            id="groups"
            class="restaurant__section"
            :items="entry?.illustrationGridAlternate?.[0]?.illustrationItems"
        >
            <template #title>{{ entry?.illustrationGridAlternate?.[0]?.title }}</template>
            <template #description>{{ entry?.illustrationGridAlternate?.[0]?.description }}</template>

            <IllustrationItem
                v-for="illustration in entry?.illustrationGridAlternate?.[0]?.illustrationItems"
                :key="illustration?.title"
                :illustration="illustration"
                :class="illustration?.sticker?.length > 12 ? 'sticker__title--small' : ''"
            >
                <template
                    v-if="illustration?.sticker"
                    #offer
                >
                    {{ illustration?.sticker }}
                </template>
                <template #title>
                    {{ illustration?.title }}
                </template>
                <template #description>
                    {{ illustration?.description }}
                </template>
                <template #cta>
                    {{ illustration?.linkField?.text }}
                </template>
            </IllustrationItem>
        </IllustrationGrid>

        <template v-if="entry?.promoBlockAlternate?.length || pending">
            <PromoBlock
                v-for="promoBlockAlternateRow in entry?.promoBlockAlternate"
                :key="promoBlockAlternateRow?.title"
                :background-color="promoBlockAlternateRow?.backgroundColor"
                :custom-background-color="promoBlockAlternateRow?.customBackgroundColor"
                :pending="pending"
                :image="promoBlockAlternateRow?.image?.[0]"
                :image-position="promoBlockAlternateRow?.imagePosition"
                :link="promoBlockAlternateRow?.linkField"
            >
                <template
                    v-if="promoBlockAlternateRow?.title"
                    #title
                >
                    {{ promoBlockAlternateRow?.title }}
                </template>

                <template
                    v-if="promoBlockAlternateRow?.description"
                    #description
                >
                    {{ promoBlockAlternateRow?.description }}
                </template>

                <template
                    v-if="promoBlockAlternateRow?.richText?.html"
                    #richText
                >
                    <div v-html="promoBlockAlternateRow?.richText?.html" />
                </template>

                <template
                    v-if="promoBlockAlternateRow?.linkField?.url || promoBlockAlternateRow?.linkField?.element"
                    #text
                >
                    <BaseArrowLink
                        :element="CraftLink"
                        :link="promoBlockAlternateRow?.linkField"
                        label-position="right"
                    >
                        {{ promoBlockAlternateRow?.linkField.text }}
                    </BaseArrowLink>
                </template>
            </PromoBlock>
        </template>

        <LazyBaseTextImage
            v-if="entry?.textImage?.[0]?.title || pending"
            :id="kebabCase(entry?.textImage?.[0]?.title)"
            class="restaurant__section"
            :background-color="entry?.textImage?.[0]?.backgroundColor"
            :image="entry?.textImage?.[0]?.image?.[0]"
            :pending="pending"
            :landscape="true"
            :use-parallax="true"
            hydrate-on-visible
        >
            <template #title>
                {{ entry?.textImage?.[0]?.title }}
            </template>

            <template
                #text
            >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="entry?.textImage?.[0]?.text?.html" />
            </template>
        </LazyBaseTextImage>

        <NewsletterBlock
            v-if="entry?.showNewsletter && entry?.newsletterBlock?.[0]"
            :image="entry?.newsletterBlock?.[0].image?.[0]"
            :pending="pending"
            :background-color="entry?.newsletterBlock?.[0]?.backgroundColor"
            :custom-background-color="entry?.newsletterBlock?.[0]?.customBackgroundColor"
            :list-id="entry?.newsletterBlock?.[0]?.listId ?? undefined"
        >
            <template
                v-if="entry?.newsletterBlock?.[0]?.title"
                #title
            >
                {{ entry.newsletterBlock[0].title }}
            </template>

            <template
                v-if="entry?.newsletterBlock?.[0]?.description"
                #description
            >
                {{ entry.newsletterBlock[0].description }}
            </template>
        </NewsletterBlock>
    </article>
</template>

<script setup>
import { kebabCase } from 'lodash-es';
import restaurantQuery from '~/graphql/queries/restaurant.graphql';

const { variables } = useCraftGraphql();

const response = await useLazyAsyncQuery({
    query: restaurantQuery,
    variables
});

const pending = response.pending;
const { craftEntry: entry } = useCraftPage(response);

const scrollItems = computed(() => {
    const items = [
        {
            entryField: 'illustrationGrid',
            label: 'restaurant.food-drinks',
            id: null
        },
        {
            entryField: 'carousselBlock',
            label: 'restaurant.happenings',
            id: null
        },
        {
            entryField: 'relatedItems',
            label: 'restaurant.events',
            id: null
        },
        {
            entryField: 'textImage',
            label: 'restaurant.contact',
            id: null
        }
    ];

    if (entry?.value?.illustrationGridAlternate?.[0]?.illustrationItems?.length) {
        // This field doesn't necessarily have a title, so we need to check if it exists
        // add between the third and last item
        items.splice(3, 0, {
            entryField: 'illustrationGridAlternate',
            label: 'restaurant.groups',
            id: 'groups'
        });
    }

    return items;
});
</script>

<style lang="less">
.property__title {
    .typography-h4();
}

.restaurant__section {
    scroll-margin: calc(var(--navbar-height) + 3.5rem - 1px); // -1px to account for subpixel rounding
}

@media ( prefers-reduced-motion: no-preference ) {
    .property[data-show-loading-state='true'] {
        .animation-main-page();
    }
}
</style>
